/**
 * Shared lazy-demand controller for realtime clients.
 *
 * Keeps the websocket connected while there is demand, then disconnects
 * after an adaptive idle delay.
 */

/** @constant */
export const DEFAULT_DEMAND_THRESHOLD_OPTIONS = {
  baseIdleMs: 10000,
  minIdleMs: 5000,
  maxIdleMs: 120000,
  resubscribeWindowMs: 12000,
  errorWindowMs: 60000,
  churnPenaltyMs: 4000,
  errorPenaltyMs: 6000,
  maxChurnPenalties: 6,
  maxErrorPenalties: 5,
  jitterMs: 250,
}

/**
 * @typedef {{
 *   connect: () => Promise<boolean|void>;
 *   disconnect: () => Promise<void>;
 *   options?: Partial<typeof DEFAULT_DEMAND_THRESHOLD_OPTIONS>;
 * }} CreateControllerOptions
 */

/**
 * @param {CreateControllerOptions} input
 */
export function createDemandThresholdController(input) {
  const options = {
    ...DEFAULT_DEMAND_THRESHOLD_OPTIONS,
    ...(input.options || {}),
  }

  let demandCount = 0
  let isConnected = false
  /** @type {Promise<boolean|void>|null} */
  let connectPromise = null
  /** @type {ReturnType<typeof setTimeout>|null} */
  let idleDisconnectTimer = null
  let lastReleaseAt = 0
  /** @type {Array<number>} */
  const churnTimestamps = []
  /** @type {Array<number>} */
  const errorTimestamps = []

  function acquire() {
    demandCount += 1
    clearIdleTimer()

    const now = Date.now()
    if (lastReleaseAt !== 0 && (now - lastReleaseAt) <= options.resubscribeWindowMs)
      churnTimestamps.push(now)

    pruneTimestampWindow(
      churnTimestamps,
      now - options.resubscribeWindowMs
    )

    return ensureConnected()
  }

  function release() {
    if (demandCount === 0)
      return Promise.resolve()

    demandCount -= 1

    if (demandCount > 0)
      return Promise.resolve()

    lastReleaseAt = Date.now()
    scheduleIdleDisconnect()

    return Promise.resolve()
  }

  function forceDisconnect() {
    clearIdleTimer()
    demandCount = 0
    connectPromise = null
    isConnected = false

    return Promise.resolve()
      .then(() => input.disconnect())
      .catch(() => undefined)
  }

  function markConnected() {
    isConnected = true
  }

  function markDisconnected() {
    isConnected = false
  }

  function recordError() {
    const now = Date.now()
    errorTimestamps.push(now)
    pruneTimestampWindow(errorTimestamps, now - options.errorWindowMs)
  }

  function getSnapshot() {
    const now = Date.now()
    pruneTimestampWindow(churnTimestamps, now - options.resubscribeWindowMs)
    pruneTimestampWindow(errorTimestamps, now - options.errorWindowMs)

    return {
      demandCount,
      isConnected,
      churnCount: churnTimestamps.length,
      errorCount: errorTimestamps.length,
      nextIdleMs: computeIdleDisconnectMs(),
      hasPendingConnect: connectPromise !== null,
      hasPendingIdleTimer: idleDisconnectTimer !== null,
    }
  }

  function ensureConnected() {
    if (isConnected)
      return Promise.resolve()

    if (connectPromise)
      return connectPromise

    connectPromise = Promise.resolve()
      .then(() => input.connect())
      .then((isReady) => {
        isConnected = isReady !== false
      })
      .catch((error) => {
        isConnected = false
        recordError()
        throw error
      })
      .finally(() => {
        connectPromise = null
      })

    return connectPromise
  }

  function scheduleIdleDisconnect() {
    clearIdleTimer()

    const idleMs = computeIdleDisconnectMs()
    idleDisconnectTimer = setTimeout(
      () => {
        idleDisconnectTimer = null

        if (demandCount > 0)
          return

        forceDisconnect()
      },
      idleMs
    )
  }

  function computeIdleDisconnectMs() {
    const now = Date.now()
    pruneTimestampWindow(churnTimestamps, now - options.resubscribeWindowMs)
    pruneTimestampWindow(errorTimestamps, now - options.errorWindowMs)

    const churnPenaltyCount = Math.min(
      churnTimestamps.length,
      options.maxChurnPenalties
    )
    const errorPenaltyCount = Math.min(
      errorTimestamps.length,
      options.maxErrorPenalties
    )

    const rawIdleMs = options.baseIdleMs
      + (churnPenaltyCount * options.churnPenaltyMs)
      + (errorPenaltyCount * options.errorPenaltyMs)

    const boundedIdleMs = clamp(rawIdleMs, options.minIdleMs, options.maxIdleMs)
    const jitter = Math.floor(Math.random() * (options.jitterMs + 1))

    return boundedIdleMs + jitter
  }

  function clearIdleTimer() {
    if (!idleDisconnectTimer)
      return

    clearTimeout(idleDisconnectTimer)
    idleDisconnectTimer = null
  }

  return {
    acquire,
    release,
    forceDisconnect,
    markConnected,
    markDisconnected,
    recordError,
    getSnapshot,
  }
}

/**
 * @param {Array<number>} timestamps
 * @param {number} minTimestamp
 */
function pruneTimestampWindow(timestamps, minTimestamp) {
  let i = 0
  while (i < timestamps.length) {
    const currentTimestamp = timestamps[i]

    if (currentTimestamp === undefined)
      break

    if (currentTimestamp >= minTimestamp) {
      i += 1
      continue
    }

    timestamps.splice(i, 1)
  }
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}
